import { useQuery } from 'react-query';
import quoterApiInstance from '../../api/quoterApiInstance';
import { QUERY_KEYS } from '../../constants/reactQuery';

/**
 * Normaliza el body de la respuesta a un booleano "el nombre ya existe".
 *
 * El endpoint puede responder el flag al nivel raíz o dentro de `data` (como el
 * resto de /my-favorite-plans), así que se leen ambas formas. Si no aparece
 * ningún flag reconocible se asume "no existe": preferimos dejar guardar y que
 * el POST falle antes que bloquear al usuario por un body inesperado.
 */
const extractExists = (body) => {
    const payload = body?.data ?? body;
    return Boolean(payload?.exists ?? payload?.nameExists ?? payload?.taken);
};

const fetchNameExists = async (planName) => {
    const { data } = await quoterApiInstance.get('/my-favorite-plans/name-exists', {
        params: { planName },
    });
    return extractExists(data);
};

/**
 * GET /my-favorite-plans/name-exists?planName=...
 *
 * Comprueba si ya hay un plan favorito con ese nombre. La query queda deshabilitada
 * mientras `planName` esté vacío (y se puede apagar con `enabled: false`, p. ej.
 * cuando el diálogo está cerrado).
 *
 * Sin cache a propósito (`staleTime`/`cacheTime` en 0): es una validación de
 * disponibilidad en tiempo real, no un dato que se pueda servir viejo. Con
 * `QUERY_CONFIG` (staleTime 5 min) el nombre recién guardado por un `POST`/`PATCH`
 * quedaba cacheado como "disponible" y volver a validarlo (o guardarlo) poco después
 * mostraba disponibilidad falsa y el guardado fallaba en el back. Ir siempre a red
 * es más simple y correcto que perseguir invalidaciones en cada mutación.
 *
 * @param   {string}  planName          nombre a validar (ya debounceado por el consumidor)
 * @param   {object}  options           opciones extra de `useQuery` (`enabled`, etc.)
 * @returns {object}  resultado de `useQuery`, con `data` === `true` si el nombre ya existe
 */
export const useFavoritePlanNameExists = (planName, options = {}) => {
    const trimmedName = (planName ?? '').trim();

    return useQuery(
        QUERY_KEYS.favoritePlans.nameExists(trimmedName),
        () => fetchNameExists(trimmedName),
        {
            ...options,
            enabled: Boolean(trimmedName) && (options.enabled ?? true),
            staleTime: 0,
            cacheTime: 0,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            retry: false,
            onError: (error) => {
                console.error('Error validating favorite plan name:', error);
                options.onError?.(error);
            },
        }
    );
};

export default useFavoritePlanNameExists;
